/**
 * ReviewFilterPills - Filter controls for image review status.
 *
 * Displays pill-style buttons for filtering images by review status.
 * Shows counts for each filter option.
 *
 * @see /src/features/gallery/pages/GalleryReviewPage.tsx - Parent component
 */

import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import type { ImageStats } from '../../context/GalleryDetailContext'

export type ReviewFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'under_review'

interface ReviewFilterPillsProps {
  /** Current active filter */
  activeFilter: ReviewFilter
  /** Called when filter changes */
  onFilterChange: (filter: ReviewFilter) => void
  /** Image statistics for counts */
  stats: ImageStats
  /** Additional className */
  className?: string
}

interface FilterOption {
  key: ReviewFilter
  label: string
  getCount: (stats: ImageStats) => number
}

const FILTER_OPTIONS: FilterOption[] = [
  { key: 'all', label: 'All', getCount: (s) => s.jobImages },
  { key: 'pending', label: 'Pending', getCount: (s) => s.pending },
  { key: 'approved', label: 'Approved', getCount: (s) => s.approved },
  { key: 'rejected', label: 'Rejected', getCount: (s) => s.rejected },
  // { key: 'under_review', label: 'Flagged', getCount: (s) => s.underReview },
]

export function ReviewFilterPills({
  activeFilter,
  onFilterChange,
  stats,
  className,
}: ReviewFilterPillsProps) {
  return (
    <div
      role="group"
      aria-label="Filter images by status"
      className={cn('flex flex-wrap gap-2', className)}
    >
      {FILTER_OPTIONS.map((option) => {
        const count = option.getCount(stats)
        const isActive = activeFilter === option.key

        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onFilterChange(option.key)}
            aria-pressed={isActive}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
              'transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm font-semibold ring-2 ring-primary/20'
                : 'bg-muted/50 text-muted-foreground font-medium hover:bg-muted hover:text-foreground'
            )}
          >
            {option.label}
            <Badge
              variant={isActive ? 'secondary' : 'outline'}
              className={cn(
                'px-1.5 py-0 text-xs tabular-nums min-w-[1.25rem] justify-center',
                isActive
                  ? 'bg-primary-foreground/20 text-primary-foreground border-transparent'
                  : 'bg-transparent'
              )}
            >
              {count}
            </Badge>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Compact filter dropdown for narrow layouts.
 */
export function ReviewFilterSelect({
  activeFilter,
  onFilterChange,
  stats,
  className,
}: ReviewFilterPillsProps) {
  return (
    <select
      value={activeFilter}
      onChange={(e) => onFilterChange(e.target.value as ReviewFilter)}
      aria-label="Filter images by status"
      className={cn(
        'px-3 py-2 rounded-md border bg-background text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {FILTER_OPTIONS.map((option) => {
        const count = option.getCount(stats)
        return (
          <option key={option.key} value={option.key}>
            {option.label} ({count})
          </option>
        )
      })}
    </select>
  )
}
